import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBrand, usePageTitle } from '@aco/shared';
import type { GameType, ScoringMode } from '@aco/shared';
import { createGame, getRecentPlayers } from '../services/api.js';
import type { PlayerResult } from '../services/api.js';
import { useGameContext } from '../context/GameContext.js';
import { useAuth } from '../context/AuthContext.js';
import { AuthBar } from '../components/AuthBar.js';
import { PlayerSetupCard } from '../components/PlayerSetupCard.js';

/**
 * Standalone lobby — enter player names, pick game type, start game.
 *
 * Scoring mode is NOT selected here — it is derived from auth state:
 *   - Authenticated:  'intermediate' (PPR teaching mode)
 *   - Anonymous:      'simple'
 *
 * In the future this will come from tournament config or the player's
 * last-used preference stored on their account.
 *
 * Authenticated UX:
 *  - Player 1 auto-populated with logged-in user's displayName
 *  - All player fields are smart selectors (recent players + typeahead search + New Player...)
 *
 * Anonymous UX:
 *  - Plain text inputs
 *
 * URL-driven state: ?mode=doubles preserved on refresh.
 */
export function StandaloneLobby() {
  const brand = useBrand();
  usePageTitle(null, 'New Game');

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setGame, setLoading, setError } = useGameContext();
  const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();

  // ── URL-driven: doubles mode ──────────────────────────────
  const isDoubles = searchParams.get('mode') === 'doubles';
  const setIsDoubles = (val: boolean) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      if (val) next.set('mode', 'doubles');
      else next.delete('mode');
      return next;
    });
  };

  const [gameType, setGameType] = useState<GameType>('standard21');

  // ── Scoring mode: derived from auth + user preference ────
  // Authenticated → user's saved preference (default: 'intermediate' PPR).
  // Anonymous → simple.
  const scoringMode: ScoringMode =
    isAuthenticated
      ? (user?.preferredScoringMode ?? 'intermediate')
      : 'simple';

  // ── Player name fields ────────────────────────────────────
  const [p1a, setP1a] = useState('');
  const [p1b, setP1b] = useState('');
  const [p2a, setP2a] = useState('');
  const [p2b, setP2b] = useState('');

  // Recent players for the authenticated selector
  const [recentPlayers, setRecentPlayers] = useState<PlayerResult[]>([]);

  // ── Auto-populate Player 1 from auth ────────────────────
  useEffect(() => {
    if (isAuthenticated && user?.displayName && !p1a) {
      setP1a(user.displayName);
    }
  }, [isAuthenticated, user?.displayName]);

  // ── Fetch recent opponents once authenticated ────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    getRecentPlayers(10)
      .then(({ players }) => setRecentPlayers(players))
      .catch(() => {
        // Non-fatal — selector still works with search + new player
      });
  }, [isAuthenticated]);

  // ── Duplicate-player guard ────────────────────────────────
  const allSelectedNames = [p1a, ...(isDoubles ? [p1b] : []), p2a, ...(isDoubles ? [p2b] : [])]
    .map((n) => n.trim().toLowerCase())
    .filter(Boolean);
  const hasDuplicatePlayers = allSelectedNames.length !== new Set(allSelectedNames).size;

  const canStart =
    p1a.trim().length > 0 &&
    p2a.trim().length > 0 &&
    (!isDoubles || (p1b.trim().length > 0 && p2b.trim().length > 0)) &&
    !hasDuplicatePlayers;

  // ── Build the player `id` for game creation ──────────────
  const makePlayerId = useCallback(
    (name: string, slot: 'self' | 'other') => {
      if (slot === 'self' && isAuthenticated && user && name === user.displayName) {
        return `player:${user.userId}`;
      }
      return `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    },
    [isAuthenticated, user],
  );

  const handleStart = useCallback(async () => {
    if (!canStart) return;

    setLoading(true);
    try {
      const { game } = await createGame({
        brandId: brand.id,
        gameType,
        scoringMode,
        team1: {
          name: isDoubles ? `${p1a} & ${p1b}` : p1a,
          players: isDoubles
            ? [
                { id: makePlayerId(p1a, 'self'), name: p1a.trim() },
                { id: makePlayerId(p1b, 'other'), name: p1b.trim() },
              ]
            : [{ id: makePlayerId(p1a, 'self'), name: p1a.trim() }],
        },
        team2: {
          name: isDoubles ? `${p2a} & ${p2b}` : p2a,
          players: isDoubles
            ? [
                { id: makePlayerId(p2a, 'other'), name: p2a.trim() },
                { id: makePlayerId(p2b, 'other'), name: p2b.trim() },
              ]
            : [{ id: makePlayerId(p2a, 'other'), name: p2a.trim() }],
        },
      });

      setGame(game);
      navigate(`/game/${game.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game');
    }
  }, [canStart, brand.id, gameType, scoringMode, isDoubles, p1a, p1b, p2a, p2b, makePlayerId, setGame, setLoading, setError, navigate]);

  const containerStyle: React.CSSProperties = {
    minHeight: '100dvh',
    backgroundColor: brand.colors.background,
    color: brand.colors.text,
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '24px',
    gap: '24px',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
  };

  // ── Loading state ─────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{ ...containerStyle, justifyContent: 'center' }}>
        <div style={{ opacity: 0.5 }}>Loading…</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <h1 style={{ margin: 0, fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: 800 }}>
        {brand.shortName} Scoreboard
      </h1>

      {/* Auth bar — user info or sign-in prompt */}
      <AuthBar
        isAuthenticated={isAuthenticated}
        user={user}
        logout={logout}
        primaryColor={brand.colors.primary}
        textColor={brand.colors.text}
      />

      {/* Kiosk app download */}
      <a
        href="/downloads/KioskClient.apk"
        style={{
          display: 'inline-block',
          padding: '10px 24px',
          borderRadius: 8,
          backgroundColor: brand.colors.primary,
          color: '#fff',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '0.9rem',
        }}
      >
        Download Kiosk App
      </a>

      {/* Player setup card */}
      <PlayerSetupCard
        isAuthenticated={isAuthenticated}
        isDoubles={isDoubles}
        setIsDoubles={setIsDoubles}
        gameType={gameType}
        setGameType={setGameType}
        p1a={p1a} setP1a={setP1a}
        p1b={p1b} setP1b={setP1b}
        p2a={p2a} setP2a={setP2a}
        p2b={p2b} setP2b={setP2b}
        hasDuplicatePlayers={hasDuplicatePlayers}
        canStart={canStart}
        onStart={handleStart}
        recentPlayers={recentPlayers}
        primaryColor={brand.colors.primary}
        textColor={brand.colors.text}
        skinsEnabled={brand.features.skinsGame}
      />
    </div>
  );
}
